import { useState } from "react";
import { toast } from "sonner";
import {
  Upload,
  FileText,
  X,
  CheckCircle2,
  Loader2,
  ShieldCheck,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  soumettrePaiementHorsSysteme,
  ApiError,
  type SoumissionHorsSystemeResult,
} from "@/lib/api";
import {
  firstHorsSystemeError,
  CANAUX_HORS_SYSTEME,
  type PayerFormState,
} from "@/lib/payer-validation";
import { friendlyError } from "@/lib/errors";
import { formatXof } from "@/lib/format";

/** Minimum d'une déclaration (contrôle du serveur et de la base : 100 F). */
export const MONTANT_MIN_DECLARATION = 100;

/**
 * Montant déclaré (SCOM-04, 22/10/2026) : vide, c'est le reste choisi ;
 * sinon un entier de 100 F au reste choisi. Renvoie le montant ou le message
 * d'erreur à montrer.
 */
export function montantDeclare(
  saisie: string,
  plafond: number | null,
): { montant: number } | { erreur: string } {
  if (plafond == null || plafond <= 0) {
    return { erreur: "Choisissez le montant à régler : une tranche ou le reste du motif." };
  }
  const brut = saisie.replace(/[\s\u202f]/g, "");
  if (!brut) return { montant: plafond };
  if (!/^\d+$/.test(brut)) return { erreur: "Indiquez le montant payé, en francs, sans décimale." };
  const montant = Number(brut);
  if (montant < MONTANT_MIN_DECLARATION) {
    return { erreur: `Le montant déclaré est d'au moins ${formatXof(MONTANT_MIN_DECLARATION)}.` };
  }
  if (montant > plafond) {
    return { erreur: `Le montant déclaré dépasse le reste dû (${formatXof(plafond)}).` };
  }
  return { montant };
}

const ACCEPTED = ["image/jpeg", "image/png", "application/pdf"];
const MAX_BYTES = 5 * 1024 * 1024; // 5 Mo — miroir du contrôle backend

function fmtTaille(n: number): string {
  return n < 1024 * 1024
    ? `${Math.round(n / 1024)} Ko`
    : `${(n / (1024 * 1024)).toFixed(1)} Mo`;
}

interface Props {
  formState: PayerFormState;
  /** Matricule SEDEC retenu (élève sélectionné ou saisi). */
  eleveMatricule: string;
  /** Tranche choisie sous « Ce que vous réglez » ; `null` : reste du motif. */
  motifEcheanceId?: string | null;
}

/**
 * Onglet « j'ai déjà payé hors système » du parcours invité : le parent déclare
 * un virement / dépôt / chèque… avec une preuve (image ou PDF). La déclaration
 * part en attente de validation par l'économe — elle ne déclenche aucun paiement
 * en ligne et ne compte pour le motif qu'une fois validée.
 */
export function HorsSystemeForm({ formState, eleveMatricule, motifEcheanceId = null }: Props) {
  // Plafond : le reste exact de la tranche ou du motif choisi (formState.montantXof).
  const plafond = formState.montantXof;
  const [montantSaisi, setMontantSaisi] = useState("");
  const [canal, setCanal] = useState("");
  const [banque, setBanque] = useState("");
  const [referenceExterne, setReferenceExterne] = useState("");
  const [datePaiement, setDatePaiement] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<SoumissionHorsSystemeResult | null>(null);

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    if (!f) {
      setFile(null);
      return;
    }
    if (!ACCEPTED.includes(f.type)) {
      toast.error("Format non accepté : image JPEG/PNG ou PDF uniquement.");
      e.target.value = "";
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("Fichier trop volumineux (5 Mo maximum).");
      e.target.value = "";
      return;
    }
    setFile(f);
  }

  async function submit() {
    const problem = firstHorsSystemeError(formState, {
      canal,
      datePaiement,
      hasFile: !!file,
    });
    if (problem) {
      toast.error(problem);
      return;
    }
    const m = montantDeclare(montantSaisi, plafond);
    if ("erreur" in m) {
      toast.error(m.erreur);
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("etablissementId", formState.ecoleId!);
      fd.append("motifId", formState.motifId!);
      fd.append("montantXof", String(m.montant));
      // La tranche choisie part au serveur : il plafonne au reste de la tranche.
      if (motifEcheanceId) fd.append("motifEcheanceId", motifEcheanceId);
      fd.append("canal", canal);
      fd.append("eleveMatricule", eleveMatricule);
      fd.append("payerNom", formState.payer.nom);
      fd.append("payerPrenoms", formState.payer.prenoms);
      fd.append("payerEmail", formState.payer.email);
      fd.append("payerTelephone", formState.payer.telephone);
      if (banque.trim()) fd.append("banque", banque.trim());
      if (referenceExterne.trim())
        fd.append("referenceExterne", referenceExterne.trim());
      fd.append("datePaiement", datePaiement);
      fd.append("preuve", file!);

      const data = await soumettrePaiementHorsSysteme(fd);
      setResult(data);
      toast.success("Déclaration enregistrée : en attente de validation.");
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        // Seul un 404 « élève introuvable » parle du matricule : un 404 de
        // route ou de motif ne doit pas accuser le matricule du parent.
        if (err.status === 404 && /élève|eleve|matricule/i.test(err.message))
          toast.error("Matricule introuvable pour cette école. Vérifiez-le.");
        else if (err.status === 413)
          toast.error("Fichier trop volumineux (5 Mo maximum).");
        else
          toast.error(friendlyError(err));
      } else {
        toast.error(friendlyError(err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    return (
      <div
        className="rounded-xl border border-sage/30 bg-sage/5 p-6 text-center"
        data-testid="hs-success"
      >
        <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-sage" />
        <p className="font-bold text-navy">Déclaration enregistrée</p>
        <p className="mt-1 text-sm text-navy/70">
          Référence <span className="font-mono font-bold">{result.reference}</span>.
          L'économe de l'école va vérifier votre preuve. Le paiement comptera pour
          le motif une fois validé.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="hs-form">
      <div className="flex items-start gap-2 rounded-xl border border-orange-50 bg-cream p-3 text-xs text-navy/70">
        <ShieldCheck size={16} className="mt-0.5 shrink-0 text-sage" />
        <span>
          Vous avez payé par virement, dépôt en banque ou chèque ? Déclarez-le ici
          avec une preuve. Aucun débit en ligne : l'économe valide votre preuve
          avant que le paiement ne compte pour le motif.
        </span>
      </div>

      <div>
        <label htmlFor="hs-montant" className="mb-1 block text-xs font-bold text-navy">
          Montant payé (FCFA)
        </label>
        <Input
          id="hs-montant"
          inputMode="numeric"
          value={montantSaisi}
          onChange={(e) => setMontantSaisi(e.target.value)}
          placeholder={plafond != null ? String(plafond) : ""}
          data-testid="hs-montant"
        />
        <p className="mt-1 text-xs text-navy/60" data-testid="hs-plafond">
          {plafond != null && plafond > 0
            ? `Au plus ${formatXof(plafond)}, le reste dû choisi ci-dessus. Laissez vide si vous avez payé tout ce reste.`
            : "Choisissez d'abord ce que vous réglez."}
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-bold text-navy">
          Moyen utilisé
        </label>
        <select
          value={canal}
          onChange={(e) => setCanal(e.target.value)}
          data-testid="hs-canal"
          className="w-full rounded-xl border border-orange-100 bg-white px-3 py-2.5 text-sm text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40"
        >
          <option value="">Sélectionnez</option>
          {CANAUX_HORS_SYSTEME.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-bold text-navy">
            Banque <span className="font-normal text-navy/40">(optionnel)</span>
          </label>
          <Input
            value={banque}
            onChange={(e) => setBanque(e.target.value)}
            placeholder="Ex. SGBCI, NSIA…"
            data-testid="hs-banque"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-bold text-navy">
            Référence du virement{" "}
            <span className="font-normal text-navy/40">(optionnel)</span>
          </label>
          <Input
            value={referenceExterne}
            onChange={(e) => setReferenceExterne(e.target.value)}
            placeholder="N° de l'opération"
            data-testid="hs-reference"
          />
        </div>
      </div>

      <div>
        <label className="mb-1 block text-xs font-bold text-navy">
          Date du paiement
        </label>
        <Input
          type="date"
          value={datePaiement}
          onChange={(e) => setDatePaiement(e.target.value)}
          data-testid="hs-date"
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-bold text-navy">
          Preuve (image JPEG/PNG ou PDF, 5 Mo max)
        </label>
        {file ? (
          <div
            className="flex items-center justify-between gap-3 rounded-xl border border-sage/30 bg-sage/5 px-4 py-3"
            data-testid="hs-file-chip"
          >
            <div className="flex min-w-0 items-center gap-2">
              <FileText size={18} className="shrink-0 text-sage" />
              <span className="truncate text-sm text-navy">{file.name}</span>
              <span className="shrink-0 text-xs text-navy/50">
                {fmtTaille(file.size)}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setFile(null)}
              data-testid="hs-file-remove"
              className="shrink-0 text-navy/50 hover:text-coral"
              aria-label="Retirer le fichier"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-orange-200 bg-white px-4 py-6 text-sm text-navy/60 hover:border-navy/40">
            <Upload size={18} />
            Choisir un fichier
            <input
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              onChange={onPickFile}
              data-testid="hs-file-input"
              className="hidden"
            />
          </label>
        )}
      </div>

      <Button
        onClick={submit}
        disabled={submitting}
        data-testid="hs-submit"
        className="w-full rounded-xl bg-coral py-6 text-lg font-bold text-white shadow-lg hover:bg-[#d16b50]"
      >
        {submitting ? (
          <>
            <Loader2 size={20} className="animate-spin" /> Envoi…
          </>
        ) : (
          "Envoyer la déclaration"
        )}
      </Button>
    </div>
  );
}

export default HorsSystemeForm;
